import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CountrySelector } from '@cotyorg/ccx-organism-country-selector';
import Button from '@cotyorg/ccx-atoms/Button';
import withRedux from '../withRedux';
import ObserverBar from '../Observer';
import HeaderActions from '../HeaderActions';
import HeaderLogo from '../HeaderLogo';
import HeaderNavToggle from '../HeaderNavToggle';

const generateHeaderCssClass = (fixed, themeName, isPinned, isNavigationVisible) =>
  classNames(
    'header',
    'header-minimal', {
      'header--fixed': fixed,
      [`header--theme-${themeName}`]: themeName && !isPinned && !isNavigationVisible,
    },
  );

const generateLogoCssClass = logoPosition =>
  classNames(
    'cell',
    'align-middle',
    'flex-container',
    'header-minimal__logo', {
      'xsmall-order-1 auto': logoPosition === 'left',
      shrink: logoPosition !== 'left',
    },
  );

const generateMobileNavCssClass = logoPosition =>
  classNames(
    'cell',
    'align-middle',
    'flex-container',
    'header-minimal__country-selector', {
      'xsmall-order-3 shrink': logoPosition === 'left',
      auto: logoPosition !== 'left',
    },
  );

const generateActionsCssClass = logoPosition =>
  classNames(
    'cell',
    'align-right',
    'align-middle',
    'flex-container',
    'header-minimal__actions', {
      'xsmall-order-2 shrink': logoPosition === 'left',
      auto: logoPosition !== 'left',
    },
  );

const generateToggleIcon = isCountrySelectorVisible => (isCountrySelectorVisible ? '-' : '+');

const HeaderMinimal = ({
  themeName,
  showSearch,
  showMiniCart,
  showCountrySelector = false,
  logoSrc,
  logoAlt = '',
  fixed,
  isPinned,
  setHeaderFixed,
  logoPosition,
  navImageSrc,
  navImageSrcSet,
  mobileNavType,
  isCountrySelectorVisible,
  toggleCountrySelectorVisibility,
  isNavigationVisible,
  searchLink,
  cartLink,
  toggleSearchVisibility,
  searchIconSrc,
  miniCartIconSrc,
  leftChevronIconSrc,
  rightChevronIconSrc,
  closeIconSrc,
  hamburgerIconSrc,
  content: {
    mobileNavButtonOpenText,
    mobileNavButtonCloseText,
    searchButtonText,
    logoLinkText,
  } = {},
  homeLink,
  cartQuantity,
  miniCart,
  toggleMiniCart,
  currentBreakpoint,
}) => (
  <Fragment>
    <ObserverBar setHeaderFixed={setHeaderFixed} fixed={fixed} />
    <header className={generateHeaderCssClass(fixed, themeName, isPinned, isNavigationVisible)}>
      <CountrySelector />
      <div className="grid-container">
        <div className="grid-x grid-padding-x">
          <HeaderNavToggle
            navCssClass={generateMobileNavCssClass(logoPosition)}
            isMinimalHeader
            navImageSrc={navImageSrc}
            navImageSrcSet={navImageSrcSet}
            navType={mobileNavType}
            leftChevronIconSrc={leftChevronIconSrc}
            rightChevronIconSrc={rightChevronIconSrc}
            closeIconSrc={closeIconSrc}
            hamburgerIconSrc={hamburgerIconSrc}
            mobileNavButtonOpenText={mobileNavButtonOpenText}
            mobileNavButtonCloseText={mobileNavButtonCloseText}
          />
          <HeaderLogo
            logoCssClass={generateLogoCssClass(logoPosition)}
            logoSrc={logoSrc}
            logoAlt={logoAlt}
            logoLinkText={logoLinkText}
            homeLink={homeLink}
          />
          <div className={generateActionsCssClass(logoPosition)}>
            {
              (showCountrySelector &&
                <div className="header__country-selector">
                  <Button
                    theme="clear"
                    extraClass="header__country-selector-button"
                    onClick={toggleCountrySelectorVisibility}
                  >
                    United Kingdom (En) {generateToggleIcon(isCountrySelectorVisible)}
                  </Button>
                </div>
              )
            }
            <HeaderActions
              showSearch={showSearch}
              showMiniCart={showMiniCart}
              cartQuantity={cartQuantity}
              searchLink={searchLink}
              cartLink={cartLink}
              onSearchClick={toggleSearchVisibility}
              searchIconSrc={searchIconSrc}
              miniCartIconSrc={miniCartIconSrc}
              searchButtonText={searchButtonText}
              miniCart={miniCart}
              toggleMiniCart={toggleMiniCart}
              currentBreakpoint={currentBreakpoint}
            />
          </div>
        </div>
      </div>
    </header>
  </Fragment>
);

HeaderMinimal.propTypes = {
  showSearch: PropTypes.bool,
  showMiniCart: PropTypes.bool,
  showCountrySelector: PropTypes.bool,
  fixed: PropTypes.bool,
  logoSrc: PropTypes.string.isRequired,
  logoAlt: PropTypes.string,
  themeName: PropTypes.oneOf(['filled', 'transparent']),
  logoPosition: PropTypes.oneOf(['left', 'center']),
  mobileNavType: PropTypes.string,
  navImageSrc: PropTypes.string,
  navImageSrcSet: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  })),
  isPinned: PropTypes.bool,
  setHeaderFixed: PropTypes.func,
  isCountrySelectorVisible: PropTypes.bool,
  toggleCountrySelectorVisibility: PropTypes.func,
  toggleSearchVisibility: PropTypes.func,
  isNavigationVisible: PropTypes.bool,
  searchLink: PropTypes.string,
  cartLink: PropTypes.string,
  searchIconSrc: PropTypes.string,
  miniCartIconSrc: PropTypes.string,
  leftChevronIconSrc: PropTypes.string,
  rightChevronIconSrc: PropTypes.string,
  closeIconSrc: PropTypes.string,
  hamburgerIconSrc: PropTypes.string,
  content: PropTypes.shape({
    mobileNavButtonOpenText: PropTypes.string,
    mobileNavButtonCloseText: PropTypes.string,
    searchButtonText: PropTypes.string,
    logoLinkText: PropTypes.string,
  }).isRequired,
  homeLink: PropTypes.string,
  cartQuantity: PropTypes.number,
  miniCart: PropTypes.func,
  toggleMiniCart: PropTypes.func,
  currentBreakpoint: PropTypes.string,
};

export default withRedux(HeaderMinimal);
